import { EntityCommonInfoModel, FilteredPagedQueryResult, FilteredPagedModel, CommonMultiDeleteModel } from "../../entities/common";
import { apiSlice, TagTypes } from "./apiSlice";
import { getQuerySearchParams } from "../../utils";
import { OieModel } from "../../entities/oie";

export const oieApi = apiSlice.injectEndpoints({
  endpoints: (builder) => ({
    getOieCommonInfo: builder.query<EntityCommonInfoModel, undefined>({
      query: () => `/oie/commonInfo`,
      providesTags: [{ type: TagTypes.Oie, id: "LIST" }],
    }),
    getOieFilteredPaged: builder.query<FilteredPagedQueryResult<OieModel>, FilteredPagedModel>({
      query: ({ page = 1, pageSize = 10, searchTerm = "", sortFieldName = "", descending = false, ...rest }: FilteredPagedModel) => {
        let url = `/oie/pageable?page=${page}&pageSize=${pageSize}&searchTerm=${searchTerm}&sortFieldName=${sortFieldName}&descending=${descending}`;
        const restParams = getQuerySearchParams(rest);
        if (restParams) {
          url += `&${restParams}`;
        }

        return url;
      },
      providesTags: (result, error, arg) =>
        result
          ? [
              { type: TagTypes.Oie, id: "PARTIAL-LIST" },
              ...result.data.map(({ id }: { id: number | string }) => ({
                type: TagTypes.Oie,
                id,
              })),
            ]
          : [{ type: TagTypes.Oie, id: "PARTIAL-LIST" }],
    }),
    getOie: builder.query<OieModel, number>({
      query: (oieId) => `/oie/getById/${oieId}`,
      providesTags: (result, error, arg) => [{ type: TagTypes.Oie, id: arg }],
    }),
    scrapOie: builder.query<OieModel, { tracingNumber: string }>({
      query: ({ tracingNumber }) => ({
        url: "/oie/scrap",
        method: "POST",
        body: { tracingNumber },
      }),
      providesTags: [{ type: TagTypes.Oie, id: "Scrap" }],
    }),
    addOie: builder.mutation({
      query: (initialOie) => ({
        url: "/oie",
        method: "POST",
        body: initialOie,
      }),
      invalidatesTags: [
        { type: TagTypes.Oie, id: "LIST" },
        { type: TagTypes.Oie, id: "PARTIAL-LIST" },
      ],
    }),
    editOie: builder.mutation({
      query: (oie) => ({
        url: `/oie/${oie.id}`,
        method: "PUT",
        body: oie,
      }),
      invalidatesTags: (result, error, arg) => [{ type: TagTypes.Oie, id: arg.id }],
    }),
    deleteOie: builder.mutation({
      query: (oieId) => ({
        url: `/oie/${oieId}`,
        method: "DELETE",
      }),
      invalidatesTags: (result, error, arg) =>
        error
          ? []
          : [
              { type: TagTypes.Oie, id: arg },
              { type: TagTypes.Oie, id: "LIST" },
            ],
    }),
    deleteMultiOies: builder.mutation({
      query: (multiDeleteModel: CommonMultiDeleteModel) => ({
        url: "/oie/multiDelete",
        method: "DELETE",
        body: multiDeleteModel,
      }),
      invalidatesTags: (result, error, arg) =>
        error
          ? []
          : [
              ...arg.ids.map((id: number) => ({
                type: TagTypes.Oie,
                id,
              })),
              { type: TagTypes.Oie, id: "LIST" },
            ],
    }),
  }),
});

export const {
  useGetOieCommonInfoQuery,
  useGetOieFilteredPagedQuery,
  useGetOieQuery,
  useAddOieMutation,
  useEditOieMutation,
  useScrapOieQuery,
  useLazyScrapOieQuery,
  useDeleteOieMutation,
  useDeleteMultiOiesMutation,
} = oieApi;
